/**
 * Review feedback on a document: comment on a passage, decide, draft.
 *
 * Three roles meet in one panel, and which one you are is decided by the rights
 * the SERVER resolved — never by a guess here:
 *
 * - a **reviewer** (`feedback`) selects a passage and says what is wrong with
 *   it, as many times as they need, and submits them together. Reviewing is not
 *   editing, which is the whole point of a right that is not `edit`.
 * - an **editor** (`edit`) sees every reviewer's comments, accepts or rejects
 *   each one, and can route the set through the approval chain first where a
 *   document cannot be revised on one person's say-so.
 * - the same editor then **drafts** a new version from everything accepted —
 *   across all reviewers, not one at a time.
 *
 * Two details that look small and are not:
 *
 * **Feedback is bound to the version the reviewer read.** A comment on "the
 * second paragraph" is meaningless once a new version shifts the paragraphs,
 * and worse, could be applied to text nobody objected to. Every item stores the
 * passage AS SELECTED, so a quote can still be found in a revised document even
 * when its offsets have moved.
 *
 * **Drafting produces a DRAFT.** The approved release keeps being served until
 * that draft clears the ordinary version approval. Approving a review approves
 * the changes; it does not publish a document nobody has read yet.
 */

import { useCallback, useEffect, useState } from 'react'
import type { DmsClient, DmsDocumentDetail, DmsReview, DmsReviewItem } from './contracts'

export interface DocumentReviewProps {
  client: DmsClient
  documentId: string
  detail: DmsDocumentDetail
  versionId: string
  busy?: boolean
  onChanged?: () => void
}

interface DraftItem {
  kind: 'rewrite' | 'comment'
  suggestion: string
  anchor_text: string
  page_no: number
  section_label: string
}

export function DocumentReview({
  client, documentId, detail, versionId, busy, onChanged,
}: DocumentReviewProps) {
  const canComment = Boolean(detail.rights.feedback) && Boolean(client.submitReview)
  const canDecide = Boolean(detail.rights.edit) && Boolean(client.listReviews)
  const [reviews, setReviews] = useState<DmsReview[]>([])
  const [items, setItems] = useState<DraftItem[]>([])
  const [summary, setSummary] = useState('')
  const [working, setWorking] = useState(false)
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    if (!client.listReviews) return
    try {
      setReviews(await client.listReviews({ documentId }))
    } catch {
      // A panel that cannot list reviews still has to let one be written.
      setReviews([])
    }
  }, [client, documentId])

  useEffect(() => { void load() }, [load])

  if (!canComment && !canDecide) return null

  const run = async (fn: () => Promise<unknown>) => {
    setWorking(true)
    setNote('')
    try {
      await fn()
      await load()
      onChanged?.()
    } catch (error) {
      setNote(error instanceof Error ? error.message : 'That could not be done.')
    } finally {
      setWorking(false)
    }
  }

  const addItem = () => {
    // Pre-fill from the reader's current selection: they have almost always
    // just highlighted the passage they are about to complain about.
    const selected = String(window.getSelection?.()?.toString() || '').trim()
    setItems((prior) => [...prior, {
      kind: 'comment', suggestion: '', page_no: 0, section_label: '',
      anchor_text: selected.slice(0, 2000),
    }])
  }

  const patch = (index: number, key: keyof DraftItem, value: string | number) =>
    setItems((prior) => prior.map((item, i) =>
      (i === index ? { ...item, [key]: value } : item)))

  const accepted = reviews.flatMap((r) => r.items || [])
    .filter((i) => i.status === 'accepted').length

  return (
    <section style={sectionStyle}>
      <h3 style={headingStyle}>Review feedback</h3>

      {canComment ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map((item, index) => (
            <div key={index} style={cardStyle}>
              <div style={rowStyle}>
                <select
                  value={item.kind}
                  onChange={(e) => patch(index, 'kind', e.target.value)}
                  style={inputStyle}
                >
                  <option value="comment">Comment on this passage</option>
                  <option value="rewrite">Suggest a re-write</option>
                </select>
                <input
                  style={{ ...inputStyle, width: 70 }}
                  type="number" min={0} placeholder="page"
                  value={item.page_no || ''}
                  onChange={(e) => patch(index, 'page_no', Number(e.target.value) || 0)}
                />
                <input
                  style={{ ...inputStyle, flex: 1, minWidth: 80 }}
                  placeholder="section (optional)"
                  value={item.section_label}
                  onChange={(e) => patch(index, 'section_label', e.target.value)}
                />
                <button type="button" style={btnStyle}
                        onClick={() => setItems((p) => p.filter((_, i) => i !== index))}>
                  Remove
                </button>
              </div>
              <textarea
                style={{ ...inputStyle, width: '100%', minHeight: 44 }}
                placeholder="The passage you are commenting on — paste or select it in the document"
                value={item.anchor_text}
                onChange={(e) => patch(index, 'anchor_text', e.target.value)}
              />
              <textarea
                style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                placeholder={item.kind === 'rewrite'
                  ? 'What it should say instead'
                  : 'What is wrong with it'}
                value={item.suggestion}
                onChange={(e) => patch(index, 'suggestion', e.target.value)}
              />
            </div>
          ))}
          <div style={rowStyle}>
            <button type="button" style={btnStyle} onClick={addItem} disabled={busy}>
              {items.length ? 'Add another comment' : 'Add a comment'}
            </button>
            {items.length ? (
              <>
                <input
                  style={{ ...inputStyle, flex: 1, minWidth: 120 }}
                  placeholder="Summary of this review (optional)"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
                <button
                  type="button"
                  style={primaryBtnStyle}
                  disabled={working || busy
                    || !items.some((i) => i.suggestion.trim() || i.anchor_text.trim())}
                  onClick={() => run(async () => {
                    await client.submitReview?.({
                      documentId, versionId, summary,
                      items: items.filter((i) => i.suggestion.trim() || i.anchor_text.trim()),
                    })
                    setItems([])
                    setSummary('')
                  })}
                >
                  Submit {items.length} comment{items.length === 1 ? '' : 's'}
                </button>
              </>
            ) : null}
          </div>
          <p style={mutedStyle}>
            Your comments are recorded against the version you are reading, so
            they stay meaningful after the document changes.
          </p>
        </div>
      ) : null}

      {reviews.length ? (
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              canDecide={canDecide}
              working={working || Boolean(busy)}
              onDecide={(acceptedIds, rejectedIds) => run(() =>
                client.decideReview!({ reviewId: review.id,
                                       accepted: acceptedIds,
                                       rejected: rejectedIds }))}
              onApprove={client.approveReview
                ? () => run(() => client.approveReview!({ reviewId: review.id }))
                : undefined}
            />
          ))}
        </div>
      ) : (
        <p style={mutedStyle}>No review feedback on this document yet.</p>
      )}

      {canDecide && accepted > 0 && client.draftRevision ? (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            style={primaryBtnStyle}
            disabled={working || busy}
            onClick={() => run(() => client.draftRevision!({ documentId, versionId }))}
          >
            Draft a new version from {accepted} accepted comment
            {accepted === 1 ? '' : 's'}
          </button>
          <p style={mutedStyle}>
            Creates a DRAFT version carrying every accepted comment from every
            reviewer. The approved release keeps being served until that draft
            is approved in its own right.
          </p>
        </div>
      ) : null}

      {note ? <p style={errorStyle}>{note}</p> : null}
    </section>
  )
}

function ReviewCard({ review, canDecide, working, onDecide, onApprove }: {
  review: DmsReview
  canDecide: boolean
  working: boolean
  onDecide: (accepted: string[], rejected: string[]) => void
  onApprove?: () => void
}) {
  const [picked, setPicked] = useState<Record<string, 'accept' | 'reject'>>({})
  const open = (review.items || []).filter((i) => i.status === 'open')
  const decidedCount = (review.items || []).length - open.length

  const submit = () => {
    const accepted = Object.entries(picked)
      .filter(([, v]) => v === 'accept').map(([k]) => k)
    const rejected = Object.entries(picked)
      .filter(([, v]) => v === 'reject').map(([k]) => k)
    if (!accepted.length && !rejected.length) return
    onDecide(accepted, rejected)
    setPicked({})
  }

  return (
    <div style={cardStyle}>
      <div style={rowStyle}>
        <strong style={{ fontSize: 14 }}>
          {review.summary || 'Review'}
        </strong>
        <span style={mutedStyle}>
          by {review.user_key || 'unknown'} · {STATUS_LABELS[review.status] || review.status}
          {decidedCount ? ` · ${decidedCount} decided` : ''}
        </span>
      </div>
      <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'grid', gap: 6 }}>
        {(review.items || []).map((item) => (
          <li key={item.id} style={itemStyle}>
            <ItemBody item={item} />
            {canDecide && item.status === 'open' ? (
              <div style={rowStyle}>
                <label style={checkLabelStyle}>
                  <input
                    type="radio"
                    name={`decide-${item.id}`}
                    checked={picked[item.id] === 'accept'}
                    onChange={() => setPicked((p) => ({ ...p, [item.id]: 'accept' }))}
                  />
                  Accept
                </label>
                <label style={checkLabelStyle}>
                  <input
                    type="radio"
                    name={`decide-${item.id}`}
                    checked={picked[item.id] === 'reject'}
                    onChange={() => setPicked((p) => ({ ...p, [item.id]: 'reject' }))}
                  />
                  Reject
                </label>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {canDecide ? (
        <div style={{ ...rowStyle, marginTop: 8 }}>
          <button type="button" style={btnStyle} disabled={working}
                  onClick={submit}>
            Record decisions
          </button>
          {onApprove && review.status !== 'in_approval' ? (
            <button type="button" style={btnStyle} disabled={working}
                    onClick={onApprove}
                    title="Route this review for sign-off before it is implemented">
              Send for approval
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function ItemBody({ item }: { item: DmsReviewItem }) {
  const where = [item.page_no ? `page ${item.page_no}` : '', item.section_label || '']
    .filter(Boolean).join(' · ')
  return (
    <>
      <div style={rowStyle}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {item.kind === 'rewrite' ? 'Re-write' : 'Comment'}
        </span>
        {where ? <span style={mutedStyle}>{where}</span> : null}
        <span style={{ ...mutedStyle, marginLeft: 'auto' }}>
          {ITEM_LABELS[item.status] || item.status}
        </span>
      </div>
      {item.anchor_text ? (
        <blockquote style={quoteStyle}>{item.anchor_text}</blockquote>
      ) : null}
      {item.suggestion ? (
        <p style={{ margin: '4px 0 0', fontSize: 14 }}>{item.suggestion}</p>
      ) : null}
    </>
  )
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'draft',
  submitted: 'submitted',
  in_approval: 'awaiting approval',
  applied: 'drafted into a new version',
  rejected: 'rejected',
}

const ITEM_LABELS: Record<string, string> = {
  open: 'undecided',
  accepted: 'accepted',
  rejected: 'rejected',
  applied: 'in a draft version',
}

const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 12,
}
const headingStyle: React.CSSProperties = {
  margin: '0 0 8px', fontSize: 14, fontWeight: 600,
}
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 6, padding: 8,
  display: 'grid', gap: 6,
}
const itemStyle: React.CSSProperties = {
  borderLeft: '2px solid var(--color-border)', paddingLeft: 8,
}
const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
}
const inputStyle: React.CSSProperties = {
  padding: '4px 6px', border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: 13,
  fontFamily: 'inherit',
}
const btnStyle: React.CSSProperties = {
  ...inputStyle, cursor: 'pointer',
}
const primaryBtnStyle: React.CSSProperties = {
  ...btnStyle, fontWeight: 600,
  borderColor: 'var(--color-accent)',
}
const checkLabelStyle: React.CSSProperties = {
  display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 13,
}
const quoteStyle: React.CSSProperties = {
  margin: '4px 0 0', padding: '2px 8px', fontSize: 13,
  borderLeft: '3px solid var(--color-border)',
  color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap',
}
const mutedStyle: React.CSSProperties = {
  color: 'var(--color-text-secondary)', fontSize: 13,
}
const errorStyle: React.CSSProperties = {
  color: 'var(--color-error)', fontSize: 13, marginTop: 8,
}
